import React, { useEffect, useMemo, useState } from "react";

import {
  AlertCircle,
  Check,
  CheckCircle2,
  Circle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Home,
  Loader2,
  type LucideIcon,
  ShieldCheck,
  User,
  UserCheck,
  XCircle,
} from "lucide-react";
import dayjs from "dayjs";

import { cn } from "../../../../lib/utils";

import PreviewPdf from "js/components/Contracts/PreviewPdf";
import { File as RequirementFile, Requirement } from "js/components/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "js/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "js/components/ui/alert";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { Skeleton } from "js/components/ui/skeleton";
import { StatusBadge } from "js/components/ui/status-badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "js/components/ui/tooltip";

import { getFile } from "../../../../api/tenants";
import { useDownloadTenantFolderZip } from "hooks/useTenantReportDownloads";
import { toast } from "utils/toast";

const STATUS_SORT_ORDER: Record<string, number> = {
  invalid: 0,
  pending_upload: 1,
  preparing: 2,
  reviewing: 3,
  pending_review: 3,
  valid: 4,
};

interface RequirementDetailsMultiexpedienteComponentProps {
  requirements: object;
  tenantReportId: string;
  reportUrl: string;
  multiexpedienteFolderNumber?: string | null;
  expedienteHasDocuments?: boolean;
}

type StatusKey = "valid" | "reviewing" | "pending_review" | "preparing" | "pending_upload" | "invalid";

const STATUS_MAP: Record<StatusKey, { status: "success" | "warning" | "info" | "error"; label: string }> = {
  valid: { status: "success", label: "Validado" },
  reviewing: { status: "warning", label: "En revision" },
  pending_review: { status: "warning", label: "En revision" },
  preparing: { status: "info", label: "Por cargar" },
  pending_upload: { status: "info", label: "Por cargar" },
  invalid: { status: "error", label: "Invalidado" },
};

const templateMap: Record<string, string> = {
  "tenant-pf": "Inquilino (Persona Fisica)",
  "tenant-pf-cja": "Inquilino (Persona Fisica)",
  "tenant-pm": "Inquilino (Persona Moral)",
  "tenant-pm-cja": "Inquilino (Persona Moral)",
  "guarantor-pf": "Obligado solidario (Persona Fisica)",
  "guarantor-pf-cja": "Obligado solidario (Persona Fisica)",
  "guarantor-pm": "Obligado solidario (Persona Moral)",
  "guarantor-pm-cja": "Obligado solidario (Persona Moral)",
  "guarantor-investigation-pf": "Obligado solidario (Persona Fisica)",
  "guarantor-investigation-pm": "Obligado solidario (Persona Moral)",
  collateral: "Propiedad en garantia",
  "lease-contract-pf": "Contrato de arrendamiento (Persona Fisica)",
  "lease-contract-pm": "Contrato de arrendamiento (Persona Moral)",
  "owner-representative": "Representante del propietario",
};

// Role metadata for each document group (design 9a).
function roleInfo(key: string): {
  label: string;
  legal: string | null;
  isPerson: boolean;
  icon: LucideIcon;
  avatar: string;
} {
  const legal = key.includes("-pf")
    ? "Persona física"
    : key.includes("-pm")
      ? "Persona moral"
      : null;
  if (key.startsWith("tenant"))
    return { label: "Inquilino", legal, isPerson: true, icon: User, avatar: "bg-blue-50 text-blue-700" };
  if (key.startsWith("guarantor"))
    return { label: "Obligado solidario", legal, isPerson: true, icon: ShieldCheck, avatar: "bg-orange-50 text-orange-700" };
  if (key.startsWith("landlord"))
    return { label: "Propietario", legal, isPerson: true, icon: User, avatar: "bg-purple-50 text-purple-700" };
  if (key.startsWith("owner-representative"))
    return { label: "Representante del propietario", legal, isPerson: true, icon: UserCheck, avatar: "bg-purple-50 text-purple-700" };
  if (key.startsWith("lease-contract"))
    return { label: "Contrato de arrendamiento", legal: null, isPerson: false, icon: FileText, avatar: "bg-slate-100 text-slate-600" };
  if (key === "collateral")
    return { label: "Propiedad en garantía", legal: null, isPerson: false, icon: Home, avatar: "bg-teal-50 text-teal-700" };
  return { label: key, legal, isPerson: false, icon: FileText, avatar: "bg-muted text-muted-foreground" };
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// form_values is a list of { key, value, type, label } (key = english slug, label =
// spanish display). We match against key/name/label so it works regardless of encoding.
const FIRST_NAME_IDS = ["first_name", "nombre", "nombre(s)", "nombres", "primer nombre"];
const SECOND_NAME_IDS = ["second_name", "segundo nombre"];
const FIRST_LAST_IDS = ["first_last_name", "primer apellido", "apellido paterno"];
const SECOND_LAST_IDS = ["second_last_name", "segundo apellido", "apellido materno"];
const COMPANY_IDS = [
  "business_legal_name",
  "razon social",
  "razón social",
  "nombre de la empresa",
  "denominacion social",
  "denominación social",
];

// Best-effort person/company name from a subfolder's identity form values.
function nameFromRequirements(reqs: Requirement[]): string | null {
  const pick = (ids: string[]): string | undefined => {
    for (const req of reqs) {
      const fvs = (req as any).form_values;
      if (!Array.isArray(fvs)) continue;
      for (const fv of fvs) {
        if (!fv?.value) continue;
        const candidates = [fv.key, fv.name, fv.label]
          .filter(Boolean)
          .map((s: string) => s.toString().trim().toLowerCase());
        if (candidates.some((c) => ids.includes(c))) return String(fv.value);
      }
    }
    return undefined;
  };
  const person = [
    pick(FIRST_NAME_IDS),
    pick(SECOND_NAME_IDS),
    pick(FIRST_LAST_IDS),
    pick(SECOND_LAST_IDS),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  return person || pick(COMPANY_IDS) || null;
}

function RequirementStatusIcon({ status }: { status: string }) {
  switch (status) {
    case "valid":
      return <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />;
    case "invalid":
      return <XCircle className="size-4 shrink-0 text-red-500" />;
    case "reviewing":
    case "pending_review":
      return <Clock className="size-4 shrink-0 text-amber-500" />;
    default:
      return <Circle className="size-4 shrink-0 text-muted-foreground/40" />;
  }
}


const RequirementDetailsMultiexpedienteComponent: React.FC<
  RequirementDetailsMultiexpedienteComponentProps
> = ({
  requirements,
  tenantReportId,
  reportUrl,
  multiexpedienteFolderNumber,
  expedienteHasDocuments = false,
}) => {
  const downloadZipMutation = useDownloadTenantFolderZip();
  const expedienteReady =
    multiexpedienteFolderNumber != null && expedienteHasDocuments;

  const handleDownloadZip = () => {
    if (!expedienteReady) {
      toast.error("El expediente no tiene documentos para descargar.");
      return;
    }
    downloadZipMutation.mutate(tenantReportId, {
      onSuccess: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `expediente-${multiexpedienteFolderNumber}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      onError: (error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status && status >= 400 && status < 500) {
          toast.error("El expediente no tiene documentos para descargar.");
          return;
        }
        toast.error(
          "Ocurrió un error al descargar los archivos. Por favor intente nuevamente.",
        );
      },
    });
  };

  const [selectedRequirement, setSelectedRequirement] = useState<any>(null);
  const [selectedRequirementModalVisible, setSelectedRequirementModalVisible] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [pdfBlobs, setPdfBlobs] = useState<{ fileId: string; pdfBlob: Blob }[]>(
    [],
  );
  const [imageUrls, setImageUrls] = useState<{ fileId: string; url: string }[]>(
    [],
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPdfBlob, setSelectedPdfBlob] = useState<Blob | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [subfolders, setSubfolders] = useState<
    {
      key: string;
      name: string;
      personName: string | null;
      requirements: Requirement[];
    }[]
  >([]);

  // Total progress across all subfolders
  const { totalRequirements, totalValid, totalPercentage } = useMemo(() => {
    const total = subfolders.reduce((acc, sf) => acc + sf.requirements.length, 0);
    const valid = subfolders.reduce(
      (acc, sf) => acc + sf.requirements.filter((r) => r.status === "valid").length,
      0,
    );
    return {
      totalRequirements: total,
      totalValid: valid,
      totalPercentage: total > 0 ? Math.round((valid / total) * 100) : 0,
    };
  }, [subfolders]);

  useEffect(() => {
    if (requirements) {
      const formattedSubfolders = Object.entries(requirements)
        .sort((a, b) => {
          const orderA = Object.keys(templateMap).indexOf(a[0]);
          const orderB = Object.keys(templateMap).indexOf(b[0]);
          return orderA - orderB;
        })
        .map(([key, value]: [string, Requirement[]]) => ({
          key,
          name: templateMap[key] || key,
          personName: nameFromRequirements(value),
          requirements: value,
        }));

      setSubfolders(formattedSubfolders);
    }
  }, [requirements]);

  const handleListItemClick = (item: any) => {
    setSelectedRequirement(item);
    setSelectedRequirementModalVisible(true);
    fetchImageUrls(item.files);
  };

  const closeRequirementModal = () => {
    setSelectedRequirementModalVisible(false);
    setSelectedRequirement(null);
  };

  const getStatusBadge = (status: string) => {
    const mapped = STATUS_MAP[status as StatusKey];
    if (!mapped) return <StatusBadge status="neutral" label={status} />;
    return <StatusBadge status={mapped.status} label={mapped.label} />;
  };

  const renderFormValues = (formValues: {
    [key: string]: { name: string; label?: string; value: string };
  }) => {
    return (
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <tbody>
            {Object.values(formValues).map((item, index) => (
              <tr key={index} className="border-b last:border-b-0">
                <td className="w-1/2 bg-muted/50 px-3 py-2 font-medium">
                  {item.name || item.label}
                </td>
                <td className="px-3 py-2">{item.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    return imageExtensions.some((extension) =>
      filename.toLowerCase().endsWith(extension),
    );
  };

  const handleDownloadFile = (file: { filename: string; file_id: string }) => {
    if (isImageFile(file.filename)) {
      const imageUrl = imageUrls.find(
        (img) => img.fileId === file.file_id,
      )?.url;
      if (imageUrl) {
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = file.filename;
        link.click();
      }
    } else {
      const pdfBlob = pdfBlobs.find(
        (pdf) => pdf.fileId === file.file_id,
      )?.pdfBlob;
      if (pdfBlob) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = file.filename;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }
    }
  };

  const fetchImageUrls = async (files: RequirementFile[]) => {
    const newUrls: { fileId: string; url: string }[] = [];
    const newPdf: { fileId: string; pdfBlob: Blob }[] = [];

    for (const file of files) {
      const existingUrl = imageUrls.find((img) => img.fileId === file.file_id);
      if (!existingUrl) {
        const response = await getFile(tenantReportId, file.file_id);
        const contentType = response.type;

        if (contentType && contentType.includes("application/pdf")) {
          const blob = new Blob([response], { type: "application/pdf" });
          newPdf.push({ fileId: file.file_id, pdfBlob: blob });
        } else {
          const imageUrl = URL.createObjectURL(response);
          newUrls.push({ fileId: file.file_id, url: imageUrl });
        }
      }
    }
    setImageUrls((prevUrls) => [...prevUrls, ...newUrls]);
    setPdfBlobs((prevBlobs) => [...prevBlobs, ...newPdf]);
  };

  const handleImageClick = (url: string) => {
    setImagePreviewUrl(url);
  };

  const handlePdfClick = (pdfBlob: Blob) => {
    setIsModalVisible(true);
    setSelectedPdfBlob(pdfBlob);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCurrentPage(1);
  };

  const renderFiles = (files: { filename: string; file_id: string }[]) => {
    return (
      <ul className="mt-3 divide-y rounded-md border">
        {files.map((file, index) => {
          const imageUrl = imageUrls.find((img) => img.fileId === file.file_id)?.url;
          const pdfBlob = pdfBlobs.find((pdf) => pdf.fileId === file.file_id)?.pdfBlob;

          return (
            <li key={index} className="flex items-center gap-3 px-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.filename}</p>
                <div className="mt-1.5">
                  {isImageFile(file.filename) ? (
                    imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="Preview"
                        className="h-[100px] max-w-[100px] cursor-pointer rounded object-cover"
                        onClick={() => handleImageClick(imageUrl)}
                      />
                    ) : (
                      <Skeleton className="h-[100px] w-[100px] rounded" />
                    )
                  ) : pdfBlob ? (
                    <div
                      className="flex h-[60px] w-[50px] cursor-pointer items-center justify-center rounded border bg-red-50"
                      onClick={() => handlePdfClick(pdfBlob)}
                    >
                      <FileText className="size-6 text-red-500" />
                    </div>
                  ) : (
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadFile(file)}
              >
                <Download className="size-3.5" />
                Descargar
              </Button>
            </li>
          );
        })}
      </ul>
    );
  };

  const allComplete =
    totalRequirements > 0 && totalValid === totalRequirements;
  const pendingTotal = totalRequirements - totalValid;
  // Soft traffic-light: orange < 50% · amber 50–99% · green 100%.
  const barColor =
    totalPercentage === 100
      ? "bg-emerald-500"
      : totalPercentage >= 50
        ? "bg-amber-500"
        : "bg-orange-500";

  return (
    <>
      {/* Progress summary + upload action (single compact block) */}
      <div className="mb-3">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-medium text-foreground">
            {totalRequirements === 0
              ? "Documentos del expediente"
              : allComplete
                ? "Expediente completo"
                : `${totalValid} de ${totalRequirements} documentos validados`}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={reportUrl == null}
              onClick={() => window.open(reportUrl)}
              aria-label="Carga de Documentos"
            >
              <ExternalLink className="size-3.5" />
              <span className="hidden sm:inline">Carga de Documentos</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={!expedienteReady || downloadZipMutation.isPending}
              onClick={handleDownloadZip}
              aria-label="Descargar Expediente"
            >
              {downloadZipMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              <span className="hidden sm:inline">Descargar Expediente</span>
            </Button>
          </div>
        </div>
        {totalRequirements > 0 && (
          <div className="space-y-1.5">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  barColor,
                )}
                style={{ width: `${totalPercentage}%` }}
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {allComplete
                  ? "Todos los documentos validados"
                  : `Faltan ${pendingTotal} por validar`}
              </span>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                {totalValid}/{totalRequirements} · {totalPercentage}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Per-group collapsible rows (design 9a) */}
      {subfolders.length > 0 && (
        <Accordion
          type="multiple"
          defaultValue={subfolders
            .filter((sf) => sf.requirements.some((r) => r.status !== "valid"))
            .map((sf) => sf.key)}
        >
          {subfolders.map((subfolder) => {
            const validCount = subfolder.requirements.filter(
              (r) => r.status === "valid",
            ).length;
            const total = subfolder.requirements.length;
            const pending = total - validCount;
            const isComplete = pending === 0;
            const info = roleInfo(subfolder.key);
            const GroupIcon = info.icon;
            const displayName =
              info.isPerson && subfolder.personName
                ? subfolder.personName
                : info.legal
                  ? `${info.label} (${info.legal})`
                  : info.label;
            const subtitle =
              info.isPerson && subfolder.personName
                ? [info.label, info.legal].filter(Boolean).join(" · ")
                : `${total} documento${total !== 1 ? "s" : ""}`;
            const initials =
              info.isPerson && subfolder.personName
                ? initialsOf(subfolder.personName)
                : null;

            const completionBadge = isComplete ? (
              <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-emerald-700">
                <Check className="size-3.5" strokeWidth={2.5} />
                {validCount}/{total} completo
              </span>
            ) : (
              <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-amber-700">
                <Clock className="size-3.5" />
                {validCount}/{total} · {pending} pendiente{pending !== 1 ? "s" : ""}
              </span>
            );

            return (
              <AccordionItem
                key={subfolder.key}
                value={subfolder.key}
                id={`docs-${subfolder.key}`}
                className={cn(
                  "mb-2 scroll-mt-4 overflow-hidden rounded-xl border transition-shadow",
                  isComplete
                    ? "border-border"
                    : "border-amber-300 shadow-[0_0_0_3px_rgba(245,158,11,.08)]",
                )}
              >
                <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
                  <div className="flex flex-1 items-start gap-3">
                    <div
                      className={cn(
                        "flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                        info.avatar,
                      )}
                    >
                      {initials ?? <GroupIcon className="size-4" />}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="line-clamp-2 text-sm font-semibold">
                        {displayName}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span>{subtitle}</span>
                        {/* On mobile the badge wraps under the name so it never gets clipped */}
                        <span className="sm:hidden">{completionBadge}</span>
                      </div>
                    </div>
                    {/* On desktop the badge sits to the right */}
                    <span className="hidden shrink-0 sm:flex">
                      {completionBadge}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="border-t px-2 pb-2 pt-1">
                  <ul className="divide-y divide-border/50">
                    {[...subfolder.requirements]
                      .sort(
                        (a, b) =>
                          (STATUS_SORT_ORDER[a.status] ?? 2) -
                          (STATUS_SORT_ORDER[b.status] ?? 2),
                      )
                      .map((item: any, reqIndex: number) => {
                        const isValid = item.status === "valid";
                        const mapped = STATUS_MAP[item.status as StatusKey];
                        const statusLabel = mapped?.label ?? item.status;
                        const tooltipText = item.updated_at
                          ? `${dayjs(item.updated_at).format("DD/MM/YYYY HH:mm")}`
                          : undefined;

                        return (
                          <li key={reqIndex}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className={cn(
                                    "flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/60",
                                    isValid && "text-muted-foreground",
                                  )}
                                  onClick={() => handleListItemClick(item)}
                                >
                                  <RequirementStatusIcon status={item.status} />
                                  <span className="min-w-0 flex-1 truncate text-left">
                                    {item.name}
                                  </span>
                                  {mapped?.status === "warning" ||
                                  mapped?.status === "error" ? (
                                    <StatusBadge
                                      status={mapped.status}
                                      label={statusLabel}
                                      showDot={false}
                                    />
                                  ) : (
                                    <span className="shrink-0 text-xs text-muted-foreground">
                                      {statusLabel}
                                    </span>
                                  )}
                                </button>
                              </TooltipTrigger>
                              {tooltipText && (
                                <TooltipContent side="top">
                                  {tooltipText}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </li>
                        );
                      })}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Requirement detail dialog */}
      <Dialog
        open={selectedRequirementModalVisible}
        onOpenChange={(open) => {
          if (!open) closeRequirementModal();
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle>{selectedRequirement?.name}</DialogTitle>
              {selectedRequirement && getStatusBadge(selectedRequirement.status)}
            </div>
            <DialogDescription className="sr-only">
              Detalle del requisito
            </DialogDescription>
          </DialogHeader>

          {selectedRequirement && (
            <div className="space-y-4">
              {selectedRequirement.status === "invalid" &&
                selectedRequirement.invalid_reason && (
                  <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Motivo de rechazo:</AlertTitle>
                    <AlertDescription>
                      {selectedRequirement.invalid_reason}
                    </AlertDescription>
                  </Alert>
                )}
              {selectedRequirement.form_values &&
                renderFormValues(selectedRequirement.form_values)}
              {selectedRequirement.files &&
                selectedRequirement.files.length > 0 &&
                renderFiles(selectedRequirement.files)}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF preview dialog */}
      <Dialog
        open={isModalVisible}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
      >
        <DialogContent className="max-h-[90vh] sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Previsualizar documento PDF</DialogTitle>
            <DialogDescription className="sr-only">
              Vista previa del documento
            </DialogDescription>
          </DialogHeader>
          {selectedPdfBlob && (
            <PreviewPdf
              pdfBlob={selectedPdfBlob}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              password={null}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog
        open={!!imagePreviewUrl}
        onOpenChange={(open) => {
          if (!open) setImagePreviewUrl(null);
        }}
      >
        <DialogContent className="sm:max-w-[80vw]">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
            <DialogDescription className="sr-only">
              Vista previa de imagen
            </DialogDescription>
          </DialogHeader>
          {imagePreviewUrl && (
            <img
              src={imagePreviewUrl}
              alt="Full-size Preview"
              className="max-h-[70vh] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequirementDetailsMultiexpedienteComponent;
